import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, RotateCcw, Trash2, Copy, Check, Pencil, UsersRound, Target, Eye } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useLanguage } from "../../context/LanguageContext";
import { tCrm, tFmt, crmApi, formatDate, formatCurrency, logDevError } from "../../lib/crm";
import { toast } from "sonner";
import { COUNTRY_TIMEZONE_MAP, countryToTimezone, COUNTRY_NAME_TO_CODE, CITY_TO_COUNTRY } from "../../lib/countryTimezones";
import { IANA_TIMEZONES } from "../../lib/timezones";

export default function CRMAgents() {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [credentials, setCredentials] = useState(null);

  const fetchAgents = useCallback(async () => {
    try {
      const r = await crmApi.get("/agents");
      setAgents(r.data);
    } catch (err) {
      logDevError("CRMAgents.fetch", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
    // Auto-refresh every 30s so admin sees presence changes live
    const id = setInterval(fetchAgents, 30_000);
    return () => clearInterval(id);
  }, [fetchAgents]);

  const resetPw = async (id, email) => {
    if (!window.confirm(tFmt(language, "agentsPage.resetPwConfirm", { email }))) return;
    try {
      const r = await crmApi.post(`/agents/${id}/reset-password`);
      setCredentials({ email, password: r.data.temp_password, email_sent: r.data.email_sent });
    } catch {
      toast.error(t("common.error"));
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(tFmt(language, "agentsPage.deleteConfirm", { name }))) return;
    try {
      await crmApi.delete(`/agents/${id}`);
      toast.success(t("agentsPage.deleted"));
      fetchAgents();
    } catch {
      toast.error(t("common.error"));
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-8" data-testid="agents-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-2">
            {t("agentsPage.slug")}
          </p>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tighter text-white">
            {t("agents.title")}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {tFmt(language, agents.length === 1 ? "agentsPage.subtitleSingular" : "agentsPage.subtitle", { n: agents.length })}
          </p>
        </div>
        <Button
          onClick={() => setShowNew(true)}
          data-testid="new-agent-btn"
          className="bg-lime-400 hover:bg-lime-300 text-black font-semibold rounded-md shadow-[0_0_20px_rgba(163,230,53,0.15)] hover:shadow-[0_0_28px_rgba(163,230,53,0.35)] transition-all"
        >
          <Plus className="w-4 h-4 mr-1.5" /> {t("agents.newAgent")}
        </Button>
      </header>

      {loading ? (
        <div className="text-zinc-500 text-sm font-mono">cargando…</div>
      ) : agents.length === 0 ? (
        <EmptyState onCreate={() => setShowNew(true)} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {agents.map((a) => (
            <AgentCard
              key={a.id}
              agent={a}
              onEdit={() => setEditing(a)}
              onAssign={() => setAssigning(a)}
              onGhost={() => navigate(`/crm/leads?agent=${a.id}&ghost=1`)}
              onResetPw={() => resetPw(a.id, a.email)}
              onDelete={() => remove(a.id, a.full_name)}
            />
          ))}
        </div>
      )}

      {showNew && (
        <NewAgentDialog
          onClose={() => setShowNew(false)}
          onCreated={(creds) => {
            setShowNew(false);
            setCredentials(creds);
            fetchAgents();
          }}
        />
      )}
      {editing && (
        <EditAgentDialog
          agent={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchAgents(); }}
        />
      )}
      {assigning && (
        <AssignLeadsDialog
          agent={assigning}
          onClose={() => setAssigning(null)}
          onDone={() => { setAssigning(null); fetchAgents(); }}
        />
      )}
      {credentials && <CredentialsDialog creds={credentials} onClose={() => setCredentials(null)} />}
    </div>
  );
}

// ============================================================
// Agent card (techy minimal)
// ============================================================
function AgentCard({ agent, onEdit, onAssign, onGhost, onResetPw, onDelete }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const initials = (agent.full_name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div
      data-testid={`agent-card-${agent.id}`}
      className="group relative flex flex-col gap-3 p-5 bg-zinc-900 border border-zinc-800 rounded-md hover:border-lime-400/40 hover:bg-zinc-900/80 transition-all"
    >
      {/* top: avatar + name + email */}
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="w-10 h-10 rounded-md bg-zinc-800 border border-zinc-700 flex items-center justify-center font-mono text-xs text-lime-300 group-hover:bg-lime-400/10 group-hover:border-lime-400/30 transition">
            {initials}
          </div>
          {agent.is_online && (
            <span
              data-testid={`online-dot-${agent.id}`}
              title={t("presence.online")}
              className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-zinc-900"
            >
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-white truncate flex items-center gap-2">
            {agent.full_name}
            {agent.is_online && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                {t("presence.online")}
              </span>
            )}
          </div>
          <div className="text-xs text-zinc-500 truncate font-mono">{agent.email}</div>
        </div>
      </div>

      {/* mid: role + goal */}
      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-zinc-800">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500">{t("agentsPage.role")}</div>
          <div className="text-sm text-zinc-200 mt-0.5">{agent.job_title || "—"}</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-500">{t("agentsPage.goal")}</div>
          <div className="text-sm font-mono text-lime-300 mt-0.5">{formatCurrency(agent.monthly_goal)}</div>
        </div>
      </div>

      {/* bottom: actions */}
      <div className="flex items-center gap-1 pt-3 border-t border-zinc-800">
        <button
          onClick={onEdit}
          data-testid={`edit-agent-${agent.id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-zinc-300 rounded hover:bg-lime-400/10 hover:text-lime-300 transition"
        >
          <Pencil className="w-3 h-3" /> {t("common.edit")}
        </button>
        <button
          onClick={onAssign}
          data-testid={`assign-leads-${agent.id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-zinc-300 rounded hover:bg-lime-400/10 hover:text-lime-300 transition"
        >
          <Target className="w-3 h-3" /> {t("agentsPage.assign")}
        </button>
        <button
          onClick={onGhost}
          data-testid={`ghost-view-${agent.id}`}
          title={t("ghost.viewPipelineBtn")}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-zinc-300 rounded hover:bg-violet-400/10 hover:text-violet-300 transition"
        >
          <Eye className="w-3 h-3" /> {t("ghost.viewPipelineBtn")}
        </button>
        <button
          onClick={onResetPw}
          data-testid={`reset-pw-${agent.id}`}
          className="inline-flex items-center justify-center w-8 h-8 text-zinc-500 rounded hover:bg-amber-500/10 hover:text-amber-400 transition"
          title={t("agents.resetPw")}
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDelete}
          data-testid={`delete-agent-${agent.id}`}
          className="inline-flex items-center justify-center w-8 h-8 text-zinc-500 rounded hover:bg-red-500/10 hover:text-red-400 transition"
          title={t("common.delete")}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* created at footer */}
      <div className="font-mono text-[9px] text-zinc-600 uppercase tracking-[0.2em]">
        {tFmt(language, "agentsPage.created", { date: formatDate(agent.created_at) })}
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  return (
    <div className="text-center py-16 bg-zinc-900 border border-dashed border-zinc-800 rounded-md">
      <UsersRound className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
      <h3 className="text-lg font-medium text-white mb-1">{t("agentsPage.empty")}</h3>
      <p className="text-sm text-zinc-500 mb-4">{t("agentsPage.emptyHint")}</p>
      <Button onClick={onCreate} className="bg-lime-400 hover:bg-lime-300 text-black font-semibold">
        <Plus className="w-4 h-4 mr-1.5" /> {t("agentsPage.emptyCta")}
      </Button>
    </div>
  );
}

// ============================================================
// Dialogs
// ============================================================
function DialogShell({ title, subtitle, onClose, children, maxWidth = "max-w-md" }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`bg-zinc-950 border border-zinc-800 rounded-lg p-6 w-full ${maxWidth} shadow-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-lime-400 mb-1">{title}</div>
            {subtitle && <h2 className="text-xl font-medium text-white">{subtitle}</h2>}
          </div>
          <button
            onClick={onClose}
            data-testid="dialog-close-btn"
            className="text-zinc-500 hover:text-white p-1 rounded hover:bg-zinc-800 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function NewAgentDialog({ onClose, onCreated }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", job_title: "", monthly_goal: 0, country: "", timezone: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await crmApi.post("/agents", form);
      onCreated({ email: form.email, password: r.data.temp_password, email_sent: r.data.email_sent });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogShell title="agents/new" subtitle={t("agents.newAgent")} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="new-agent-form">
        <F label={t("agents.name") + " *"} required testid="agent-name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
        <F label={t("agents.email") + " *"} type="email" required testid="agent-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        <div className="grid grid-cols-2 gap-3">
          <F label="Teléfono" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <F label={t("agents.role")} value={form.job_title} onChange={(v) => setForm({ ...form, job_title: v })} />
        </div>
        <F label={t("agents.goal") + " (€/mes)"} type="number" value={form.monthly_goal} onChange={(v) => setForm({ ...form, monthly_goal: parseFloat(v) || 0 })} />
        <SmartLocationPicker
          country={form.country}
          timezone={form.timezone}
          onChange={({ country, timezone }) => setForm((f) => ({ ...f, country, timezone }))}
          t={t}
        />
        <div className="flex gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-white">
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="agent-submit-btn"
            className="flex-1 bg-lime-400 hover:bg-lime-300 text-black font-semibold"
          >
            {submitting ? "…" : t("common.create")}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

function EditAgentDialog({ agent, onClose, onSaved }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const [form, setForm] = useState({
    full_name: agent.full_name || "",
    phone: agent.phone || "",
    job_title: agent.job_title || "",
    monthly_goal: agent.monthly_goal || 0,
    country: agent.country || "",
    timezone: agent.timezone || "",
    internal_notes: agent.internal_notes || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await crmApi.put(`/agents/${agent.id}`, form);
      toast.success(t("agentsPage.updated"));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogShell title={`agents/${agent.id.slice(0, 8)}`} subtitle={tFmt(language, "agentsPage.editTitle", { name: agent.full_name })} onClose={onClose} maxWidth="max-w-lg">
      <form onSubmit={submit} className="space-y-3" data-testid="edit-agent-form">
        <div className="grid grid-cols-2 gap-3">
          <F label={t("agentsPage.editFullName")} required testid="edit-name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} />
          <F label={t("agents.email")} value={agent.email} readonly />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label={t("agentsPage.editPhone")} testid="edit-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <F label={t("agentsPage.editJobTitle")} testid="edit-role" value={form.job_title} onChange={(v) => setForm({ ...form, job_title: v })} />
        </div>
        <F label={t("agentsPage.editGoal")} type="number" testid="edit-goal" value={form.monthly_goal} onChange={(v) => setForm({ ...form, monthly_goal: parseFloat(v) || 0 })} />
        <SmartLocationPicker
          country={form.country}
          timezone={form.timezone}
          onChange={({ country, timezone }) => setForm((f) => ({ ...f, country, timezone }))}
          t={t}
        />
        <div>
          <Label className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{t("agentsPage.editNotes")}</Label>
          <textarea
            value={form.internal_notes}
            onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
            data-testid="edit-notes"
            rows={3}
            className="w-full mt-1 bg-zinc-950 border border-zinc-800 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-lime-400 focus:border-lime-400 transition"
          />
        </div>
        <div className="flex gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-white">
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={saving}
            data-testid="edit-save-btn"
            className="flex-1 bg-lime-400 hover:bg-lime-300 text-black font-semibold"
          >
            {saving ? t("common.saving") : t("resources.btnSave")}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}

function AssignLeadsDialog({ agent, onClose, onDone }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const [allLeads, setAllLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [scope, setScope] = useState("unassigned"); // unassigned | other | own
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [takeN, setTakeN] = useState("");

  useEffect(() => {
    setLoading(true);
    crmApi.get("/leads", { params: { include_lost: false } })
      .then((r) => setAllLeads(r.data))
      .catch((err) => logDevError("AssignLeadsDialog.fetch", err))
      .finally(() => setLoading(false));
  }, []);

  const visibleLeads = useMemo(() => {
    let list = allLeads;
    if (scope === "unassigned") list = list.filter((l) => !l.assigned_agent_id);
    else if (scope === "other") list = list.filter((l) => l.assigned_agent_id && l.assigned_agent_id !== agent.id);
    else if (scope === "own") list = list.filter((l) => l.assigned_agent_id === agent.id);
    if (countryFilter) {
      const cf = countryFilter.toLowerCase();
      list = list.filter((l) => (l.country || "").toLowerCase() === cf);
    }
    if (stageFilter) list = list.filter((l) => l.stage === stageFilter);
    if (search.trim()) {
      // Multi-token mode: if the user pasted several names separated by newline /
      // comma / semicolon / tab, treat each one as an OR filter. Otherwise behave
      // like the previous single-substring search.
      const tokens = search
        .split(/[\n;,\t]+/)
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      if (tokens.length > 1) {
        list = list.filter((l) => {
          const company = (l.company_name || "").toLowerCase();
          const contact = (l.contact_full_name || "").toLowerCase();
          return tokens.some((tok) => company.includes(tok) || contact.includes(tok));
        });
      } else {
        const s = tokens[0] || search.trim().toLowerCase();
        list = list.filter((l) =>
          (l.company_name || "").toLowerCase().includes(s) ||
          (l.contact_full_name || "").toLowerCase().includes(s) ||
          (l.country || "").toLowerCase().includes(s)
        );
      }
    }
    return list;
  }, [allLeads, scope, search, countryFilter, stageFilter, agent.id]);

  // Multi-token parsing — used by the chip preview and the "Select all matched" CTA
  const pastedTokens = useMemo(() => {
    return search
      .split(/[\n;,\t]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }, [search]);

  // Per-token diagnostics: how many leads each pasted name matched (lets the user
  // see which names from their list aren't in the CRM).
  const tokenStats = useMemo(() => {
    if (pastedTokens.length < 2) return [];
    return pastedTokens.map((tok) => {
      const t = tok.toLowerCase();
      const count = allLeads.filter((l) => {
        if (scope === "unassigned" && l.assigned_agent_id) return false;
        if (scope === "other" && (!l.assigned_agent_id || l.assigned_agent_id === agent.id)) return false;
        if (scope === "own" && l.assigned_agent_id !== agent.id) return false;
        return (l.company_name || "").toLowerCase().includes(t) ||
               (l.contact_full_name || "").toLowerCase().includes(t);
      }).length;
      return { token: tok, count };
    });
  }, [pastedTokens, allLeads, scope, agent.id]);

  const selectAllMatched = () => {
    const next = new Set(selected);
    visibleLeads.forEach((l) => next.add(l.id));
    setSelected(next);
  };

  // Auto-select all matched leads when the user is in multi-paste mode
  // (≥2 pasted tokens). The "Assign" button at the bottom then targets exactly
  // the names typed in the search box — no manual checkbox dance required.
  // We use the count of tokens + visible ids as a fingerprint to avoid loops.
  const visibleIdsKey = useMemo(() => visibleLeads.map((l) => l.id).join(","), [visibleLeads]);
  useEffect(() => {
    if (pastedTokens.length < 2) return;
    setSelected(new Set(visibleLeads.map((l) => l.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastedTokens.length, visibleIdsKey]);

  // Country list (from leads in the current scope, excluding country filter itself)
  const availableCountries = useMemo(() => {
    const set = new Set();
    for (const l of allLeads) {
      if (scope === "unassigned" && l.assigned_agent_id) continue;
      if (scope === "other" && (!l.assigned_agent_id || l.assigned_agent_id === agent.id)) continue;
      if (scope === "own" && l.assigned_agent_id !== agent.id) continue;
      const c = (l.country || "").trim();
      if (c) set.add(c);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [allLeads, scope, agent.id]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (visibleLeads.every((l) => selected.has(l.id))) {
      const next = new Set(selected);
      visibleLeads.forEach((l) => next.delete(l.id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      visibleLeads.forEach((l) => next.add(l.id));
      setSelected(next);
    }
  };

  const selectFirstN = () => {
    const n = parseInt(takeN, 10);
    if (!n || n < 1) {
      toast.error(t("assign.invalidNumber"));
      return;
    }
    const slice = visibleLeads.slice(0, n);
    if (slice.length === 0) return;
    const next = new Set(selected);
    slice.forEach((l) => next.add(l.id));
    setSelected(next);
    toast.success(tFmt(language, "assign.selectedToast", { n: slice.length }));
  };

  const submit = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      const ids = Array.from(selected);
      // Admin has hand-picked these leads in the dialog → assign ALL of them.
      // force=true so leads currently owned by another agent are reassigned
      // without any blocking confirmation. User asked "no quantity blocks".
      const r = await crmApi.post("/leads/bulk-assign", {
        lead_ids: ids,
        agent_id: agent.id,
        force: true,
      });
      toast.success(
        tFmt(language, "assign.successToast", {
          n: r.data.updated,
          name: agent.full_name,
        })
      );
      onDone();
    } catch (err) {
      logDevError("assign leads", err);
      toast.error(t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const allChecked = visibleLeads.length > 0 && visibleLeads.every((l) => selected.has(l.id));

  return (
    <DialogShell title={`assign/${agent.id.slice(0, 8)}`} subtitle={tFmt(language, "assign.title", { name: agent.full_name })} onClose={onClose} maxWidth="max-w-2xl">
      {/* Scope tabs */}
      <div className="flex gap-1 mb-3 p-1 bg-zinc-900 border border-zinc-800 rounded-md w-fit">
        {[
          { v: "unassigned", l: t("assign.scopeUnassigned") },
          { v: "other", l: t("assign.scopeOther") },
          { v: "own", l: t("assign.scopeOwn") },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setScope(opt.v)}
            data-testid={`scope-${opt.v}`}
            className={`px-3 py-1 text-xs font-medium rounded transition ${
              scope === opt.v ? "bg-lime-400 text-black" : "text-zinc-400 hover:text-white"
            }`}
          >
            {opt.l}
          </button>
        ))}
      </div>

      {/* Search + filters row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        <textarea
          placeholder={t("assign.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="assign-search"
          rows={search.includes("\n") ? Math.min(8, search.split("\n").length + 1) : 1}
          className="bg-zinc-950 border border-zinc-800 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-lime-400 resize-none whitespace-pre-wrap"
        />
        <select
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
          data-testid="assign-country-filter"
          className="bg-zinc-950 border border-zinc-800 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-lime-400"
        >
          <option value="">{t("assign.allCountries")}</option>
          {availableCountries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Multi-paste diagnostics + "Select all matched" CTA */}
      {pastedTokens.length > 1 && (
        <div className="mb-3 p-3 rounded-md bg-cyan-500/5 border border-cyan-500/25" data-testid="assign-multi-match-bar">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-cyan-300">
                {tFmt(language, "assign.multiDetected", { n: pastedTokens.length })}
              </div>
              <div className="text-xs text-zinc-400 mt-0.5">
                {tFmt(language, "assign.multiSummary", {
                  matched: tokenStats.filter((s) => s.count > 0).length,
                  total: pastedTokens.length,
                  leads: visibleLeads.length,
                })}
              </div>
            </div>
            <Button
              type="button"
              onClick={selectAllMatched}
              data-testid="assign-select-all-matched"
              disabled={visibleLeads.length === 0}
              className="bg-cyan-400 hover:bg-cyan-300 text-black font-semibold whitespace-nowrap disabled:opacity-40"
              size="sm"
            >
              {tFmt(language, "assign.selectAllMatched", { n: visibleLeads.length })}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="assign-tokens-chips">
            {tokenStats.map((s, i) => (
              <span
                key={`${s.token}-${i}`}
                className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${
                  s.count > 0
                    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                    : "bg-rose-500/15 text-rose-300 border-rose-500/30 line-through opacity-80"
                }`}
                title={s.count > 0 ? `${s.count} match${s.count !== 1 ? "es" : ""}` : t("assign.notFound")}
              >
                {s.token}
                <span className="text-[9px] mono opacity-70">{s.count > 0 ? `×${s.count}` : "0"}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Quick bulk-select: take first N */}
      <div className="flex items-center gap-2 mb-3 p-3 rounded-md bg-lime-400/5 border border-lime-400/20">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-lime-300">
            {t("assign.bulkBlockTitle")}
          </div>
          <div className="text-xs text-zinc-400 mt-0.5">
            {t("assign.bulkBlockHint")}
          </div>
        </div>
        <Input
          type="number"
          min="1"
          placeholder="35"
          value={takeN}
          onChange={(e) => setTakeN(e.target.value)}
          data-testid="assign-take-n"
          className="w-20 bg-zinc-950 border-zinc-800 text-white text-sm text-center"
        />
        <Button
          type="button"
          onClick={selectFirstN}
          data-testid="assign-take-n-btn"
          className="bg-lime-400 hover:bg-lime-300 text-black font-semibold whitespace-nowrap"
          size="sm"
        >
          {tFmt(language, "assign.take", { n: takeN || "N" })}
        </Button>
      </div>

      {/* List */}
      <div className="border border-zinc-800 rounded-md max-h-80 overflow-y-auto" data-testid="assign-leads-list">
        {loading ? (
          <div className="p-4 text-zinc-500 text-sm font-mono">{t("common.loading").toLowerCase()}</div>
        ) : visibleLeads.length === 0 ? (
          <div className="p-6 text-center text-zinc-500 text-sm">{t("assign.empty")}</div>
        ) : (
          <>
            <label className="flex items-center gap-2 px-3 py-2 border-b border-zinc-800 bg-zinc-900/50 text-xs font-mono uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-white">
              <input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="assign-toggle-all" className="accent-lime-400" />
              {allChecked ? t("assign.deselectAll") : tFmt(language, "assign.selectAll", { n: visibleLeads.length })}
            </label>
            {visibleLeads.map((l) => (
              <label
                key={l.id}
                data-testid={`assign-row-${l.id}`}
                className="flex items-center gap-3 px-3 py-2.5 border-b border-zinc-900 hover:bg-zinc-900/60 cursor-pointer transition"
              >
                <input
                  type="checkbox"
                  checked={selected.has(l.id)}
                  onChange={() => toggle(l.id)}
                  className="accent-lime-400"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{l.company_name}</div>
                  <div className="text-xs text-zinc-500 truncate">
                    {l.contact_full_name || "—"} · {l.country || "—"} · stage: <span className="font-mono">{l.stage}</span>
                  </div>
                </div>
                {l.assigned_agent_id && l.assigned_agent_id !== agent.id && (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                    {t("assign.ownerChange")}
                  </span>
                )}
              </label>
            ))}
          </>
        )}
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-800">
        <span className="text-sm text-zinc-400 font-mono">{selected.size} {t("chips.selected")}</span>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-white">
            {t("common.cancel")}
          </Button>
          <Button
            disabled={selected.size === 0 || saving}
            onClick={submit}
            data-testid="assign-confirm-btn"
            className="bg-lime-400 hover:bg-lime-300 text-black font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? t("common.saving") : tFmt(language, "assign.assignN", { n: selected.size || "" })}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}

function CredentialsDialog({ creds, onClose }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(`Email: ${creds.email}\nPassword: ${creds.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <DialogShell title="credentials" subtitle={t("agents.tempPwTitle")} onClose={onClose}>
      <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
        {creds.email_sent
          ? t("agents.tempPwMsg").replace("{email}", creds.email)
          : "Email NOT enviado (SMTP no configurado). Copia las credenciales manualmente:"}
      </p>
      <div className="bg-black border border-zinc-800 rounded-md p-4 font-mono text-xs space-y-1.5" data-testid="credentials-dialog">
        <div><span className="text-zinc-500">Email:</span> <span className="text-white">{creds.email}</span></div>
        <div><span className="text-zinc-500">Password:</span> <span className="text-lime-300" data-testid="credentials-password">{creds.password}</span></div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={copy} data-testid="copy-credentials-btn" variant="outline" className="border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-white">
          {copied ? <><Check className="w-3.5 h-3.5 mr-1" />{t("agents.copied")}</> : <><Copy className="w-3.5 h-3.5 mr-1" />{t("agents.copy")}</>}
        </Button>
        <Button onClick={onClose} className="flex-1 bg-lime-400 hover:bg-lime-300 text-black font-semibold">
          {t("common.close")}
        </Button>
      </div>
    </DialogShell>
  );
}

// ============================================================
// Field primitives
// ============================================================
function F({ label, value, onChange, type = "text", required, testid, readonly }) {
  return (
    <div>
      <Label className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={readonly ? undefined : (e) => onChange(e.target.value)}
        required={required}
        readOnly={readonly}
        data-testid={testid}
        className={`mt-1 bg-zinc-950 border-zinc-800 text-white focus:ring-lime-400 focus:border-lime-400 ${
          readonly ? "opacity-60 cursor-not-allowed" : ""
        }`}
      />
    </div>
  );
}

function SmartLocationPicker({ country, timezone, onChange, t }) {
  // Build sorted list of unique country names (ES + EN)
  const countryOptions = useMemo(() => {
    const set = new Set(Object.keys(COUNTRY_NAME_TO_CODE));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, []);

  const [advanced, setAdvanced] = useState(false);
  const [city, setCity] = useState("");
  const [tzFilter, setTzFilter] = useState("");

  // When the user picks/types a country, auto-resolve timezone if it isn't manually overridden
  const handleCountryChange = (val) => {
    onChange({ country: val, timezone });
    const resolved = countryToTimezone(val);
    if (resolved?.tz) {
      onChange({ country: val, timezone: resolved.tz });
    }
  };

  // City fallback (used when country isn't recognized but city is)
  const handleCityChange = (val) => {
    setCity(val);
    if (!val) return;
    const code = CITY_TO_COUNTRY[val.trim().toLowerCase()];
    if (code && COUNTRY_TIMEZONE_MAP[code]) {
      onChange({ country: country || val, timezone: COUNTRY_TIMEZONE_MAP[code].tz });
    }
  };

  const resolved = countryToTimezone(country);
  const detected = resolved?.tz;
  const detectedCity = resolved?.city;
  const isAmbiguous = !!resolved?.ambiguous;

  const filteredTz = useMemo(() => {
    const q = tzFilter.trim().toLowerCase();
    if (!q) return IANA_TIMEZONES;
    return IANA_TIMEZONES.filter((z) => z.toLowerCase().includes(q));
  }, [tzFilter]);

  return (
    <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-950/50 p-3" data-testid="smart-location">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
            {t("agents.country") || "País"}
          </Label>
          <Input
            list="agent-country-list"
            value={country || ""}
            onChange={(e) => handleCountryChange(e.target.value)}
            placeholder={t("smartLoc.countryPh")}
            data-testid="smart-country-input"
            className="bg-zinc-950 border-zinc-800 text-white mt-1"
          />
          <datalist id="agent-country-list">
            {countryOptions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </div>
        <div>
          <Label className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
            {t("smartLoc.cityLabel")}
          </Label>
          <Input
            value={city}
            onChange={(e) => handleCityChange(e.target.value)}
            placeholder={t("smartLoc.cityPh")}
            data-testid="smart-city-input"
            className="bg-zinc-950 border-zinc-800 text-white mt-1"
          />
        </div>
      </div>

      {/* Detected timezone preview */}
      {timezone ? (
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-lime-400/10 border border-lime-400/30">
          <div className="text-xs">
            <span className="text-zinc-400">{t("smartLoc.detected")}: </span>
            <span className="text-lime-300 font-mono">{timezone}</span>
            {detectedCity && <span className="text-zinc-500"> · {detectedCity}</span>}
            {isAmbiguous && (
              <span className="ml-2 text-[10px] uppercase text-amber-300">
                {t("smartLoc.ambiguous")}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setAdvanced((v) => !v)}
            data-testid="smart-toggle-advanced"
            className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-lime-300"
          >
            {advanced ? t("common.cancel") : t("smartLoc.change")}
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-zinc-900 border border-zinc-800">
          <span className="text-xs text-zinc-500 italic">{t("smartLoc.noneDetected")}</span>
          <button
            type="button"
            onClick={() => setAdvanced((v) => !v)}
            data-testid="smart-toggle-advanced"
            className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-lime-300"
          >
            {advanced ? t("common.cancel") : t("smartLoc.pickManually")}
          </button>
        </div>
      )}

      {advanced && (
        <div className="space-y-1.5 border-t border-zinc-800 pt-2">
          <Input
            placeholder={t("agentsPage.tzSearchPlaceholder")}
            value={tzFilter}
            onChange={(e) => setTzFilter(e.target.value)}
            data-testid="smart-tz-filter"
            className="bg-zinc-950 border-zinc-800 text-white text-xs"
          />
          <select
            value={timezone || ""}
            onChange={(e) => onChange({ country, timezone: e.target.value })}
            data-testid="smart-tz-select"
            size={5}
            className="w-full bg-zinc-950 border border-zinc-800 text-white text-xs rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-lime-400"
          >
            <option value="">—</option>
            {filteredTz.slice(0, 200).map((tz) => (
              <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
            ))}
          </select>
          {detected && timezone && timezone !== detected && (
            <p className="text-[10px] text-amber-300 font-mono">
              {t("smartLoc.override")} (auto: {detected})
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function TimezoneSelect({ value, onChange }) {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const [filter, setFilter] = useState("");
  // Group timezones by continent prefix (Africa/, America/, Asia/, etc.)
  const grouped = useMemo(() => {
    const groups = {};
    const q = filter.trim().toLowerCase();
    for (const tz of IANA_TIMEZONES) {
      if (q && !tz.toLowerCase().includes(q)) continue;
      const prefix = tz.split("/")[0];
      (groups[prefix] = groups[prefix] || []).push(tz);
    }
    return groups;
  }, [filter]);

  const totalCount = useMemo(
    () => Object.values(grouped).reduce((sum, arr) => sum + arr.length, 0),
    [grouped]
  );

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <Label className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{t("agentsPage.editTimezone")}</Label>
        <span className="text-[10px] font-mono text-zinc-500">
          {totalCount} / {IANA_TIMEZONES.length}
        </span>
      </div>
      <Input
        placeholder={t("agentsPage.tzSearchPlaceholder")}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        data-testid="agent-timezone-filter"
        className="bg-zinc-950 border-zinc-800 text-white mb-1.5"
      />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="agent-timezone-select"
        size={6}
        className="w-full bg-zinc-950 border border-zinc-800 text-white text-sm rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-lime-400 focus:border-lime-400 transition"
      >
        <option value="">{t("agentsPage.tzUndefined")}</option>
        {Object.keys(grouped).sort().map((continent) => (
          <optgroup key={continent} label={continent}>
            {grouped[continent].map((tz) => (
              <option key={tz} value={tz}>
                {tz.replace(`${continent}/`, "").replace(/_/g, " ")}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {value && (
        <p className="text-[10px] font-mono text-lime-400 mt-1">
          {tFmt(language, "agentsPage.tzSelected", { tz: value })}
        </p>
      )}
    </div>
  );
}
